import React from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface Plan {
  id: string;
  name: string;
  api_calls_limit: number;
  max_file_size: number; // bytes
  monthly_space_limit: number; // bytes
  price_cents: number;
  is_active: boolean;
}

function formatPrice(cents: number) {
  return (cents / 100).toFixed(2);
}

function formatBytes(bytes: number) {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(0)} MB`;
}

export default function Pricing() {
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<Plan | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const { toast } = useToast();
  const { profile } = useAuth();

  React.useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true)
          .order('price_cents', { ascending: true });
        if (error) throw error;
        setPlans(data || []);
      } catch (e) {
        console.error(e);
        toast({ title: 'Error', description: 'Failed to load plans', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [toast]);

  const openUpgradeDialog = (plan: Plan) => {
    // Prevent opening for current plan
    if (profile && plan.name.toLowerCase() === profile.subscription_plan.toLowerCase()) return;
    setSelected(plan);
    setDialogOpen(true);
  };

  const handleSendEmail = () => {
    if (!selected || !profile) return;
    const subject = `Upgrade Request - ${selected.name.toUpperCase()} Plan`;
    const body = `Hello Payments Team,%0D%0A%0D%0AI would like to upgrade my MusicMasterAI subscription.%0D%0A%0D%0ARequested Plan: ${selected.name.toUpperCase()}%0D%0APrice: $${formatPrice(selected.price_cents)}/month%0D%0AAPI Calls Limit: ${selected.api_calls_limit}%0D%0AMax File Size: ${formatBytes(selected.max_file_size)}%0D%0AMonthly Storage: ${formatBytes(selected.monthly_space_limit)}%0D%0A%0D%0AMy Account Email: ${profile.email}%0D%0ACurrent Plan: ${profile.subscription_plan.toUpperCase()}%0D%0A%0D%0APlease advise on payment steps. I'll reply with payment details once completed. If I'm not sure where to make the payment, please reply with clarification.%0D%0A%0D%0AThank you!`;
    const mailto = `mailto:payments@justmusic.studio?subject=${encodeURIComponent(subject)}&body=${body}`;
    window.location.href = mailto;
  };

  const currentPlan = profile?.subscription_plan?.toLowerCase();

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Choose Your Plan</CardTitle>
          <CardDescription>Select the plan that fits your needs. Upgrades are handled via email for now.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const isCurrent = currentPlan && plan.name.toLowerCase() === currentPlan;
                return (
                  <div key={plan.id} className={`border rounded-lg p-6 flex flex-col ${isCurrent ? 'ring-2 ring-purple-500' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold capitalize">{plan.name}</h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">${formatPrice(plan.price_cents)}/month</Badge>
                        {isCurrent && <Badge className="bg-green-600 text-white">Current Plan</Badge>}
                      </div>
                    </div>
                    <ul className="text-sm text-gray-600 space-y-2 mb-6">
                      <li><span className="font-medium">API Calls:</span> {plan.api_calls_limit}</li>
                      <li><span className="font-medium">Max File Size:</span> {formatBytes(plan.max_file_size)}</li>
                      <li><span className="font-medium">Monthly Storage:</span> {formatBytes(plan.monthly_space_limit)}</li>
                    </ul>
                    <Button onClick={() => openUpgradeDialog(plan)} className="mt-auto" disabled={Boolean(isCurrent)} variant={isCurrent ? 'outline' : 'default'}>
                      {isCurrent ? 'Current Plan' : 'Upgrade Now'}
                    </Button>
                  </div>
                );
              })}
              {plans.length === 0 && (
                <div className="text-center text-gray-500 py-8">No active plans available.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Upgrade</DialogTitle>
            <DialogDescription>
              This will open your email client to send an upgrade request to payments@justmusic.studio.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="font-medium">Plan:</span> {selected.name.toUpperCase()}</div>
              <div><span className="font-medium">Price:</span> ${formatPrice(selected.price_cents)}/month</div>
              <div><span className="font-medium">API Calls:</span> {selected.api_calls_limit}</div>
              <div><span className="font-medium">Max File Size:</span> {formatBytes(selected.max_file_size)}</div>
              <div><span className="font-medium">Monthly Storage:</span> {formatBytes(selected.monthly_space_limit)}</div>
              <div className="pt-2">
                <Button onClick={handleSendEmail} className="w-full">Send Email</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
